import { useState, type FC } from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'

import CardPanduan from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/DiskusiTindakLanjut/components/CardPanduan'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SUBMITTED_FIELD_TYPES } from 'app/KinerjaPSPegawaiPage/constants/praktikKinerja'
import DialogConfirmationDiskusiTindakLanjut from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/DiskusiTindakLanjut/components/DialogConfirmationDiskusiTindakLanjut'
import BlockQuestionAnswer from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/BlockQuestionAnswer'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

import type {
  TPraktikKinerjaDiskusiTindakLanjutFormValue,
  TPraktikKinerjaPendampinganFormValue,
} from 'types/kinerja-ps-pegawai'
import type { TUsePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'

type CardFormProps = {
  formValues: TUsePelaksanaanPraktikKinerjaPS<
    TPraktikKinerjaPendampinganFormValue,
    {}
  >
  formValuesForPost: TUsePelaksanaanPraktikKinerjaPS<
    TPraktikKinerjaDiskusiTindakLanjutFormValue,
    {}
  >
  onSubmitSuccess: () => void
}

const MSG_ERR_SUBMIT = 'Gagal kirim. Coba lagi.'
const MSG_ERR_SAVE_DRAFT = 'Gagal simpan draf. Coba lagi.'
const MSG_SUCCESS_SUBMIT = 'Berhasil kirim!'
const MSG_SUCCESS_SAVE_DRAFT = 'Berhasil simpan draf!'

const FormActions: FC<{
  isSubmitting: boolean
  onSubmit: () => void
}> = ({ isSubmitting = false, onSubmit }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 w-full px-24 py-4 bg-white border-t border-subdued">
      <div className="flex justify-end gap-4 px-3 mx-auto max-w-7xl">
        <Button
          color="blue"
          size="md"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          className={clsx(
            '!px-6',
            isSubmitting && '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
          )}
          onClick={() => onSubmit?.()}
        >
          Selesaikan diskusi
        </Button>
      </div>
    </div>
  )
}

const CardForm: FC<CardFormProps> = ({
  formValues: { parsedFormValues },
  formValuesForPost: { isPostingFormValues, submitFormValues },
  onSubmitSuccess,
}) => {
  const toast = useToast()
  const { trackEvent } = usePPSTrackEvent()

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const isSubmitting = isPostingFormValues

  // Perform save action, either as draft or final submission
  const save = async ({ saveAsDraft = false }: { saveAsDraft: boolean }) => {
    try {
      await submitFormValues({
        values: { is_already_kumpulkan: true },
        saveAsDraft,
      })

      trackEvent('pelaksanaan pegawai', 'submit form C')

      toast({
        message: saveAsDraft ? MSG_SUCCESS_SAVE_DRAFT : MSG_SUCCESS_SUBMIT,
        type: 'success',
      })
      onSubmitSuccess?.()
    } catch (e) {
      console.error(e)
      toast({ message: saveAsDraft ? MSG_ERR_SAVE_DRAFT : MSG_ERR_SUBMIT })
    }
  }

  return (
    <>
      <div className="flex flex-row gap-6">
        <CardPanduan className="flex-none w-[329px]" />

        <SimpleCard className="flex-1 !rounded mb-24">
          {/* Simple Card Header */}
          <div className="flex flex-col gap-1 p-6 border-b rounded-t bg-blue-5 border-b-surface-subdued">
            <div className="space-y-1">
              <Text variant="heading-lg">
                Diskusikan pendampingan Anda bersama Tim Kinerja
              </Text>
              <Text>
                Diskusi akan diberikan hasil pemantauan dengan mempertimbangkan
                isi catatan dan survei Kepala Sekolah.
              </Text>
            </div>
          </div>

          {/* Simple Card Content */}
          <div className="flex flex-col gap-6">
            <section className="p-6 space-y-6">
              {Object.entries(
                PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SUBMITTED_FIELD_TYPES
              ).map(([fieldType, question], index) => {
                const answer = parsedFormValues?.[fieldType] || '-'

                return (
                  <BlockQuestionAnswer
                    key={index}
                    question={`${index + 1}. ${question}`}
                    answer={answer}
                  />
                )
              })}
            </section>

            <FormActions
              isSubmitting={isSubmitting || !!isDialogOpen}
              onSubmit={() => setIsDialogOpen(true)}
            />
          </div>
        </SimpleCard>
      </div>

      <DialogConfirmationDiskusiTindakLanjut
        isLoading={isSubmitting}
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false)
        }}
        onConfirm={() => {
          if (isSubmitting) return

          save({
            saveAsDraft: false,
          })
          setIsDialogOpen(false)
        }}
      />
    </>
  )
}

export default CardForm
